/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback } from 'react';
import { Platform } from 'react-native';
import { Purpose } from '../../../managers/sso';
import type { OnOtpVerified, OtpFlow } from '../types';
import { useInitiateAccountDeletion, useLocalLogout } from '../../account';
import { useEmailOtpPurposeFlow } from './useEmailOtpPurposeFlow';

export interface UseDeleteAccountOtpFlowOptions {
  email?: string;
  startTimer: () => void;
  onVerified?: OnOtpVerified;
}

export function useDeleteAccountOtpFlow({
  email,
  startTimer,
  onVerified,
}: UseDeleteAccountOtpFlowOptions): OtpFlow {
  const { mutateAsync: localLogout } = useLocalLogout();
  const initiateAccountDeletion = useInitiateAccountDeletion(
    'useDeleteAccountOtpFlow',
  );

  const performLogout = useCallback(async () => {
    await localLogout();

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const widget = (window as any).aaWidget as
        | { logout?: () => Promise<void> }
        | undefined;

      if (widget?.logout) {
        await widget.logout();
      }
    }
  }, [localLogout]);

  const handleVerified = useCallback(async () => {
    const deletionResponse = await initiateAccountDeletion.mutateAsync();
    const result = { deleteScheduledAt: deletionResponse?.deleteScheduledAt };

    const logout = async () => {
      try {
        await performLogout();
      } catch (error) {
        console.error(
          'Account deletion: logout after OTP verification failed',
          error,
        );
      }
    };

    // Web: navigate to the signed-out status screen before clearing the session.
    if (Platform.OS === 'web') {
      await onVerified?.(result);
      await logout();
      return;
    }

    await logout();
    await onVerified?.(result);
  }, [initiateAccountDeletion, onVerified, performLogout]);

  const flow = useEmailOtpPurposeFlow({
    email,
    otpPurpose: Purpose.USERNAME_CHANGE,
    startTimer,
    onVerified: handleVerified,
  });

  return {
    ...flow,
    isLoading: flow.isLoading || initiateAccountDeletion.isPending,
  };
}
